/**
 * LODEN — core.js
 * The always-loaded kernel: config, pub-sub, the custom-element base class and
 * the loader. Anything reachable only through an interaction lives in a
 * companion core-*.js, injected on first use. See docs/JS-API.md.
 *
 * @version 1.1.4
 */
(function () {
  'use strict';

  /* ================= 0. Configuration ================= */
  // `window.__AURELIA_CONFIG__` is written by theme.liquid before this runs.
  // Every value has a fallback so the theme survives that block being stripped.
  const injected = window.__AURELIA_CONFIG__ || {};

  const config = {
    routes: Object.assign(
      {
        root: '/',
        cart_url: '/cart',
        cart_add_url: '/cart/add',
        cart_change_url: '/cart/change',
        cart_update_url: '/cart/update',
        cart_clear_url: '/cart/clear',
        predictive_search_url: '/search/suggest',
        product_recommendations_url: '/recommendations/products',
      },
      injected.routes
    ),
    // Only `cartError` keeps an English fallback — the one string an error path
    // cannot proceed without. A literal would be wrong on a non-English store.
    strings: Object.assign(
      { cartError: 'There was a problem updating your cart. Please try again.' },
      injected.strings
    ),
    moneyFormat: injected.moneyFormat || '${{amount}}',
    moneyWithCurrencyFormat: injected.moneyWithCurrencyFormat || '${{amount}} USD',
    showCurrencyCode: Boolean(injected.showCurrencyCode),
    cartType: injected.cartType || 'drawer',
    prefetch: injected.prefetch !== false,
    isEditor: Boolean(window.Shopify && window.Shopify.designMode),
    breakpoints: { sm: 480, md: 750, lg: 990, xl: 1400 },
    modules: injected.modules || {},
  };

  /* ================= 1. Pub-sub ================= */
  // Cross-component communication only — never for a component to talk to itself.
  const channels = new Map();

  function on(event, handler) {
    if (!channels.has(event)) channels.set(event, new Set());
    channels.get(event).add(handler);
    return () => off(event, handler);
  }

  function off(event, handler) {
    const set = channels.get(event);
    if (set) set.delete(handler);
  }

  function emit(event, detail) {
    const set = channels.get(event);
    if (set) {
      set.forEach((handler) => {
        try {
          handler(detail);
        } catch (error) {
          console.error(`[Loden] listener for "${event}" threw`, error);
        }
      });
    }
    document.dispatchEvent(new CustomEvent(`aurelia:${event}`, { detail, bubbles: true }));
  }

  /* ================= 2. Lazy module loader ================= */
  // Each module calls `Aurelia.register(name, api)` once parsed. One promise
  // per module, so calls made before it arrives still run in order.
  const modules = Object.create(null);
  const pending = new Map();
  const resolvers = new Map();

  function load(name) {
    if (pending.has(name)) return pending.get(name);
    const promise = new Promise((resolve, reject) => {
      const url = config.modules[name];
      if (!url) return reject(new Error(`[Loden] no asset URL for "${name}"`));
      resolvers.set(name, resolve);
      const script = document.createElement('script');
      script.src = url;
      script.onerror = () => reject(new Error(`[Loden] failed to load "${name}"`));
      document.head.appendChild(script);
    });
    pending.set(name, promise);
    return promise;
  }

  function register(name, api) {
    modules[name] = api;
    const resolve = resolvers.get(name);
    if (resolve) resolve(api);
    resolvers.delete(name);
  }

  /** An async facade method forwarding to a lazily loaded module. */
  function forward(name, method) {
    return function forwarded(...args) {
      const ready = modules[name];
      if (ready) return Promise.resolve(ready[method].apply(ready, args));
      return load(name).then((api) => api[method].apply(api, args));
    };
  }

  /* ================= 3. Utilities ================= */
  // Only what a component needs synchronously at mount; the rest is core-util.js.
  function debounce(fn, wait = 200) {
    let timer;
    function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    }
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  /** Coalesces calls into one per animation frame — for scroll/resize handlers. */
  function rafThrottle(fn) {
    let frame = null;
    let lastArgs = null;
    return function rafThrottled(...args) {
      lastArgs = args;
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn.apply(this, lastArgs);
      });
    };
  }

  /** Parses an HTML string once and returns a queryable document. */
  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* ================= 4. Motion ================= */
  // `reduced` is read synchronously in nine places; the two helpers are always
  // awaited, so they forward to core-util.js.
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const motion = {
    get reduced() {
      return reducedMotionQuery.matches;
    },
    afterTransition: forward('util', 'afterTransition'),
    scrollTo: forward('util', 'scrollTo'),
  };

  /* ================= 5. Cart ================= */
  // `registerSections` and `state` stay synchronous: components call them at
  // mount, before any interaction. Ids buffer here until core-cart.js loads.
  let cartState = null;
  let cartSections = [];

  on('cart:updated', (detail) => {
    if (detail && detail.cart) cartState = detail.cart;
  });

  const cart = {
    registerSections(ids) {
      ids.forEach((id) => {
        if (id && !cartSections.includes(id)) cartSections.push(id);
      });
      if (modules.cart) modules.cart.registerSections(ids);
    },
    unregisterSection(id) {
      cartSections = cartSections.filter((existing) => existing !== id);
      if (modules.cart) modules.cart.unregisterSection(id);
    },
    /** Ids collected before core-cart.js loaded; it drains these on register. */
    get pendingSections() {
      return cartSections.slice();
    },
    get state() {
      return cartState;
    },
  };

  ['add', 'change', 'remove', 'update', 'clear', 'fetch'].forEach((method) => {
    cart[method] = forward('cart', method);
  });

  /* ================= 6. Custom element helper ================= */
  function define(name, constructor, options) {
    if (!customElements.get(name)) customElements.define(name, constructor, options);
  }

  // Every listener registered through `this.listen` is removed when the
  // element leaves the document — mandatory for Theme Editor safety.
  class AureliaElement extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      if (typeof this.mount === 'function') this.mount();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (typeof this.unmount === 'function') this.unmount();
    }

    listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, Object.assign({ signal: this.signal }, options));
    }

    /** Subscribes to a Loden channel with automatic teardown. */
    subscribe(event, handler) {
      const unsubscribe = on(event, handler);
      this.signal.addEventListener('abort', unsubscribe, { once: true });
      return unsubscribe;
    }

    $(selector) {
      return this.querySelector(selector);
    }

    $$(selector) {
      return Array.from(this.querySelectorAll(selector));
    }
  }

  /* ================= 7. Boot ================= */
  // --viewport-height backs the sticky-header offset, so it is set at paint.
  function initViewportUnits() {
    const update = rafThrottle(() => {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    });
    update();
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
  }

  // `is-keyboard` tracking lives in core-a11y.js: it is focus styling, and the
  // first Tab press loads that module anyway.
  function initGlobalKeys() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') emit('escape');
    });
  }

  // Warmed on first interaction: a11y so the first drawer open does not pay a
  // round trip while focus moves, util because `motion.afterTransition` must
  // observe a running transition — late, and a closing panel vanishes.
  function initWarmup() {
    const warm = () => {
      load('a11y').catch(() => {});
      load('util').catch(() => {});
      if (config.prefetch) load('prefetch').catch(() => {});
    };
    const options = { once: true, passive: true, capture: true };
    ['pointerover', 'pointerdown', 'touchstart', 'keydown'].forEach((t) => {
      document.addEventListener(t, warm, options);
    });
  }

  const utils = { debounce, rafThrottle, parseHTML };
  ['throttle', 'replaceFrom', 'mediaQuery', 'getScrollbarWidth', 'formatMoney', 'interpolate'].forEach(
    (m) => {
      utils[m] = forward('util', m);
    }
  );

  const a11y = {};
  ['trapFocus', 'releaseFocus', 'lockScroll', 'unlockScroll', 'announce', 'focusableWithin'].forEach(
    (m) => {
      a11y[m] = forward('a11y', m);
    }
  );

  window.Aurelia = {
    config,
    on,
    off,
    emit,
    define,
    load,
    register,
    Element: AureliaElement,
    fetchSection: forward('section', 'fetchSection'),
    cart,
    motion,
    utils,
    a11y,
  };

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  initViewportUnits();
  initGlobalKeys();
  initWarmup();

  if (config.isEditor) load('editor').catch(() => {});
})();
